import React, { useState } from 'react';

const BACKEND_URL = 'https://familybandhan-backend-933326390640.asia-south1.run.app';
const RAZORPAY_KEY_ID = 'rzp_live_SzDTGG6uT555jW';

export default function UpgradeModal({ familyEmail, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpgrade = async () => {
    setLoading(true);
    setError('');

    try {
      // Step 1: Create order on backend
      const orderRes = await fetch(`${BACKEND_URL}/api/billing/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ familyEmail }),
      });

      const orderData = await orderRes.json();

      if (!orderRes.ok) throw new Error(orderData.error || 'Failed to create order');

      // Step 2: Open Razorpay Checkout popup
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'FamilyBandhan',
        description: 'Family Premium — 1 Year',
        order_id: orderData.orderId,
        prefill: { email: familyEmail },
        theme: { color: '#7C3AED' },
        handler: async function (response) {
          // Step 3: Verify payment on backend
          try {
            const verifyRes = await fetch(`${BACKEND_URL}/api/billing/verify-payment`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                familyEmail,
              }),
            });

            const verifyData = await verifyRes.json();

            if (!verifyRes.ok) throw new Error(verifyData.error || 'Verification failed');

            // Payment verified — notify parent
            onSuccess();
          } catch (err) {
            setError('Payment received but verification failed. Please contact support@familybandhan.in');
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>

        {/* Header */}
        <div style={styles.header}>
          <span style={styles.crown}>👑</span>
          <h2 style={styles.title}>Upgrade to Family Premium</h2>
          <p style={styles.subtitle}>Preserve your family legacy — complete and forever</p>
        </div>

        {/* Price */}
        <div style={styles.priceBox}>
          <span style={styles.price}>₹99</span>
          <span style={styles.pricePer}> / year per family</span>
        </div>

        {/* Features */}
        <div style={styles.features}>
          <div style={styles.featureItem}>
            <span style={styles.tick}>✅</span>
            <div>
              <strong>Family Gallery</strong>
              <p style={styles.featureDesc}>One place for all family photos across all generations</p>
            </div>
          </div>
          <div style={styles.featureItem}>
            <span style={styles.tick}>✅</span>
            <div>
              <strong>Family Traditions</strong>
              <p style={styles.featureDesc}>Record customs, rituals, and stories for future generations</p>
            </div>
          </div>
          <div style={styles.featureItem}>
            <span style={styles.tick}>✅</span>
            <div>
              <strong>Family Events</strong>
              <p style={styles.featureDesc}>Track birthdays, anniversaries, reunions — past and upcoming</p>
            </div>
          </div>
        </div>

        {/* Error */}
        {error && <p style={styles.error}>{error}</p>}

        {/* Buttons */}
        <button
          onClick={handleUpgrade}
          disabled={loading}
          style={loading ? { ...styles.upgradeBtn, opacity: 0.7 } : styles.upgradeBtn}
        >
          {loading ? 'Opening payment...' : '✨ Upgrade for ₹99/year'}
        </button>

        <button onClick={onClose} style={styles.cancelBtn}>
          Maybe later
        </button>

        <p style={styles.note}>
          Secure payment via Razorpay · UPI, Cards, NetBanking accepted
        </p>

      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.6)', zIndex: 1000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    background: '#1a0533', border: '1px solid #7C3AED',
    borderRadius: '16px', padding: '32px 28px',
    maxWidth: '420px', width: '100%',
    color: '#f5f0eb', textAlign: 'center',
    maxHeight: '90vh', overflowY: 'auto',
  },
  header: { marginBottom: '20px' },
  crown: { fontSize: '40px' },
  title: { color: '#FFD700', fontSize: '22px', margin: '8px 0 4px' },
  subtitle: { color: '#b39ddb', fontSize: '14px', margin: 0 },
  priceBox: {
    background: '#2d0a4e', borderRadius: '12px',
    padding: '14px', marginBottom: '20px',
  },
  price: { fontSize: '36px', fontWeight: 'bold', color: '#FFD700' },
  pricePer: { fontSize: '16px', color: '#b39ddb' },
  features: { textAlign: 'left', marginBottom: '24px' },
  featureItem: {
    display: 'flex', gap: '12px', alignItems: 'flex-start',
    marginBottom: '14px',
  },
  tick: { fontSize: '18px', marginTop: '2px' },
  featureDesc: { fontSize: '13px', color: '#b39ddb', margin: '2px 0 0' },
  error: {
    background: '#4a0000', color: '#ff6b6b',
    padding: '10px', borderRadius: '8px',
    fontSize: '13px', marginBottom: '16px',
  },
  upgradeBtn: {
    width: '100%', padding: '14px',
    background: 'linear-gradient(135deg, #7C3AED, #FFD700)',
    color: '#1a0533', fontWeight: 'bold', fontSize: '16px',
    border: 'none', borderRadius: '10px', cursor: 'pointer',
    marginBottom: '10px',
  },
  cancelBtn: {
    width: '100%', padding: '10px',
    background: 'transparent', color: '#b39ddb',
    border: '1px solid #4a2070', borderRadius: '10px',
    cursor: 'pointer', fontSize: '14px', marginBottom: '14px',
  },
  note: { fontSize: '12px', color: '#6b5a7e', margin: 0 },
};
